import { Plugin } from '@nuxt/types';
import { loadScripts } from '@root/common/utils/scriptLoader';
import { resolvePromises } from '@root/common/utils/resolvePromises';

interface GaaPlugin {
  initGaa(handleUnlockArticle: () => Promise<string>, handleShowPaywall: () => Promise<void>): void;
}

declare module 'vue/types/vue' {
  interface Vue {
    $gaa: GaaPlugin;
  }
}

interface AnonymousNoAccess {
  granted: false;
}

interface RegisteredNoAccess {
  id: string;
  registrationTimestamp: number;
  granted: false;
}

interface RegisteredSubscriber {
  id: string;
  registrationTimestamp: number;
  subscriptionTimestamp: number;
  granted: true;
  grantReason: 'SUBSCRIBER';
}

type UserState = AnonymousNoAccess | RegisteredNoAccess | RegisteredSubscriber;

interface GaaMeteringInitArgument {
  googleApiClientId: string;
  userState: UserState;
  showcaseEntitlement: string;
  allowedReferrers: string[];
  handleLoginPromise: () => Promise<void>;
  registerUserPromise: () => Promise<void>;
  paywallType: 'SERVER_SIDE';
  showPaywall: () => Promise<void>;
}

interface GaaWindow extends Window {
  GaaMetering: {
    init(args: GaaMeteringInitArgument): void;
  };
}

declare let window: GaaWindow;

const gaa: Plugin = async (ctx, inject) => {
  const { store, app } = ctx;
  const meta = app.$channelConfig('meta');
  const baseUrl = meta.base.baseUrl;

  // Confidential Docs, access needed - https://developers.google.com/news/subscribe/extended-access/integration-steps/web-implementation?feed=server-side#pseudo-code
  const isShowcase = () => {
    const referrer = document.referrer;
    const url = new URL(window.location.href);

    const googleDomainRe = /google\.com/g;
    const gaaParamsList = ['gaa_at', 'gaa_ts', 'gaa_n', 'gaa_sig'];
    const gaaParams: Record<string, string | undefined> = gaaParamsList.reduce((acc: Record<string, string | undefined>, param) => {
      acc[param] = url.searchParams.get(param) || '';

      return acc;
    }, {});

    return gaaParams.gaa_at && gaaParams.gaa_ts && gaaParams.gaa_n && gaaParams.gaa_sig && googleDomainRe.test(referrer);
  };

  const loadGaaScripts = async (): Promise<boolean> => {
    const gsiScript = () => loadScripts('https://accounts.google.com/gsi/client', { async: true, defer: true });
    const swgScript = () => loadScripts('https://news.google.com/swg/js/v1/swg.js', { async: true, 'subscriptions-control': 'manual' });
    const swgGaaScript = () => loadScripts('https://news.google.com/swg/js/v1/swg-gaa.js', { async: true });

    const scriptPromises = await Promise.allSettled([gsiScript(), swgScript(), swgGaaScript()]);
    return scriptPromises.every((scriptPromise) => scriptPromise.status === 'fulfilled');
  };

  const getUserStateForActiveArticle = () => {
    let userState: UserState = {
      granted: false,
    };

    const paywall = store.state.article.activeArticle?.content.paywall;
    const paywallEnabled = paywall?.enabled;
    const paywallAccess = paywall?.access;
    const articleAccess = !paywallEnabled || (paywallEnabled && paywallAccess);
    const registrationTimestamp = Math.floor(Date.UTC(2025, 0, 1) / 1000);
    const subscriptionStartDate = store.state.piano.subscriptionInfo?.startDate;

    if (articleAccess && subscriptionStartDate) {
      userState = {
        id: store.state.piano.profile.uid,
        registrationTimestamp,
        subscriptionTimestamp: Math.floor(new Date(subscriptionStartDate).getTime() / 1000),
        granted: true,
        grantReason: 'SUBSCRIBER',
      };
    } else if (store.state.piano.isLoggedIn) {
      userState = {
        id: store.state.piano.profile.uid,
        registrationTimestamp,
        granted: false,
      };
    }

    return userState;
  };

  const initGaa = async (handleUnlockArticle: () => Promise<string>, handleShowPaywall: () => Promise<void>) => {
    if (!isShowcase()) {
      return;
    }

    const scriptsLoadResult = await loadGaaScripts();

    if (!scriptsLoadResult) {
      console.error('Failed to load GAA scripts');
      return;
    }

    const isScriptInitedWatcher = store.watch(
      (state) => state.piano.isScriptInited,
      async (isScriptInited: boolean) => {
        if (!isScriptInited) {
          return;
        }

        const entitlement = await handleUnlockArticle();

        if (!entitlement) {
          await resolvePromises();
          isScriptInitedWatcher();
          return;
        }

        window.GaaMetering.init({
          googleApiClientId: '383607936234.apps.googleusercontent.com',
          paywallType: 'SERVER_SIDE',
          showcaseEntitlement: entitlement,
          userState: getUserStateForActiveArticle(),
          allowedReferrers: [baseUrl],
          handleLoginPromise: () => store.dispatch('piano/showLoginModal', 'login'),
          registerUserPromise: () => store.dispatch('piano/showLoginModal', 'register'),
          showPaywall: handleShowPaywall,
        });

        await resolvePromises();
        isScriptInitedWatcher();
      },
      {
        immediate: true,
      }
    );
  };

  const injectableObject: GaaPlugin = {
    initGaa,
  };

  inject('gaa', injectableObject);
};

export default gaa;
